import { useState } from 'react'
import { Link } from 'react-router'
import { Download, Upload } from 'lucide-react'
import { Callout, ImportDialog, PageHeader } from '@/components/common'
import { Button } from '@/components/ui/button'
import { Card } from '@/components/ui/card'
import { useBreadcrumb } from '@/hooks/useBreadcrumb'
import { useAllPermissions, usePermission } from '@/hooks/usePermission'
import { downloadFile } from '@/lib/api/client'
import { bulkPaths } from '@/lib/bulk'
import { useDataMigrationSummary, type DataMigrationSummary } from '../api'

interface ImportStep {
  title: string
  body: string
  /** The API prefix the bulk endpoints hang off. */
  prefix: string
  /** What the dialog calls the rows — "Import {what}". */
  what: string
  /** Every permission the import needs; the button is hidden without them all. */
  permissions: string[]
  invalidateKey: readonly unknown[]
  count?: keyof DataMigrationSummary
  countLabel?: string
}

/**
 * The onboarding imports, in the order they have to happen.
 *
 * <p>Each file refers to what an earlier one created — a salary names an employee, an
 * employee names a department — so the order is the instructions, and a page listing them in
 * any other order would be teaching people to get "no employee with code …" two hundred times.
 * Every import already lived on its own screen; this one collects them for the week before
 * go-live, when somebody needs them all and does not know where each one is.
 */
const STEPS: ImportStep[] = [
  {
    title: 'Employees',
    body: 'Everybody on the payroll, with their department, job and join date. Each person gets an employee code that every later file uses.',
    prefix: '/v1/employees', what: 'employees', permissions: ['employee:write'],
    invalidateKey: ['employees'], count: 'employees', countLabel: 'employees',
  },
  {
    title: 'Bank accounts',
    body: 'Where each person is paid. The IBAN is checked the way the bank checks it, so a typo is caught here instead of on payday.',
    prefix: '/v1/employee-bank-accounts', what: 'bank accounts', permissions: ['employee:write'],
    invalidateKey: ['employees'], count: 'bankAccounts', countLabel: 'accounts',
  },
  {
    title: 'Documents',
    body: 'Passports, visas, Emirates IDs and labour cards — numbers and dates only. Expiry reminders start working for them straight away.',
    prefix: '/v1/employee-documents', what: 'documents', permissions: ['document:write'],
    invalidateKey: ['documents'], count: 'documents', countLabel: 'documents',
  },
  {
    title: 'Salaries',
    body: 'What each person is paid today, one row per pay element — basic, housing, transport. Later pay rises go through Salary revisions.',
    prefix: '/v1/payroll/employee-salaries', what: 'opening salaries', permissions: ['payroll:write'],
    invalidateKey: ['payroll'], count: 'employeesWithPay', countLabel: 'people with pay',
  },
  {
    title: 'Opening leave balances',
    body: 'How many days each person has left, as of a date. After the import, that is exactly what they can apply for.',
    prefix: '/v1/leave/opening-balances', what: 'opening balances', permissions: ['leave:configure'],
    invalidateKey: ['leave'], count: 'leaveBalances', countLabel: 'balances this year',
  },
  {
    title: 'Opening air tickets',
    body: 'When each person last flew, and how much has built up towards their next ticket. Monthly accrual carries on from the month after.',
    prefix: '/v1/payroll/opening-air-tickets', what: 'air tickets',
    permissions: ['payroll:process', 'payroll:write'],
    invalidateKey: ['payroll'], count: 'airTicketHistory', countLabel: 'tickets recorded',
  },
  {
    title: 'Opening provisions (gratuity)',
    body: 'End-of-service, leave salary and air-ticket balances from your old books, as of a month end. Monthly provisioning continues from there without counting earlier months again.',
    prefix: '/v1/payroll/opening-provisions', what: 'opening provisions', permissions: ['payroll:process'],
    invalidateKey: ['payroll'], count: 'openingProvisions', countLabel: 'balances carried in',
  },
  {
    title: 'Opening loans',
    body: 'Loans and salary advances still being repaid. Payroll starts deducting from the month you give — no approval is needed again.',
    prefix: '/v1/loans/migrated', what: 'opening loans', permissions: ['loan:approve'],
    invalidateKey: ['loans'], count: 'migratedLoans', countLabel: 'loans carried in',
  },
]

const SETUP_LINKS = [
  { to: '/org/masters', label: 'Org masters' },
  { to: '/org/establishments', label: 'Establishments' },
  { to: '/org/masters', label: 'Departments' },
  { to: '/payroll/elements', label: 'Pay elements' },
  { to: '/leave/types', label: 'Leave types' },
]

function StepNumber({ n }: { n: number }) {
  return (
    <span className="flex size-7 shrink-0 items-center justify-center rounded-full bg-[var(--color-tint-blue)] text-sm font-semibold text-[var(--color-primary)]">
      {n}
    </span>
  )
}

function StepCard({
  n, step, summary, onImport,
}: { n: number; step: ImportStep; summary?: DataMigrationSummary; onImport: () => void }) {
  const allowed = useAllPermissions(step.permissions)
  const count = step.count && summary ? summary[step.count] : undefined

  return (
    <Card className="flex flex-col gap-3 p-4 sm:flex-row sm:items-start">
      <StepNumber n={n} />
      <div className="min-w-0 flex-1 space-y-1">
        <h2 className="text-sm font-semibold text-[var(--color-heading)]">{step.title}</h2>
        <p className="text-sm text-[var(--color-muted)]">{step.body}</p>
        {count !== undefined && (
          <p className="text-xs text-[var(--color-muted)]">
            Already loaded: <span className="font-medium tabular-nums">{count}</span> {step.countLabel}
          </p>
        )}
      </div>
      {allowed ? (
        <div className="flex shrink-0 flex-wrap gap-2">
          <Button
            type="button" variant="outline" size="sm"
            onClick={() => {
              void downloadFile(bulkPaths(step.prefix).template, `${step.what}-import-template.xlsx`)
            }}
          >
            <Download className="size-4" aria-hidden="true" />
            Template
          </Button>
          <Button type="button" size="sm" onClick={onImport}>
            <Upload className="size-4" aria-hidden="true" />
            Check & import
          </Button>
        </div>
      ) : (
        <p className="shrink-0 text-xs text-[var(--color-muted)]">
          You do not have permission for this step.
        </p>
      )}
    </Card>
  )
}

export function DataMigrationPage() {
  useBreadcrumb()
  const canCount = usePermission('employee:read-all')
  const summary = useDataMigrationSummary(canCount)
  const [active, setActive] = useState<ImportStep | null>(null)

  return (
    <div className="space-y-4">
      <PageHeader
        title="Data migration"
        subtitle="Moving from spreadsheets? Load your people, pay and balances here, one file at a time."
      />

      <Callout title="Import in this order">
        Each step uses what the one before created. Every import is checked first and is
        all-or-nothing — if one row is wrong, nothing is saved until the file is fixed.
      </Callout>

      <Card className="flex flex-col gap-3 p-4 sm:flex-row sm:items-start">
        <StepNumber n={1} />
        <div className="space-y-1">
          <h2 className="text-sm font-semibold text-[var(--color-heading)]">Company setup</h2>
          <p className="text-sm text-[var(--color-muted)]">
            Before any file: set up your establishments, departments, designations and grades, the
            pay elements you pay, and your leave types. The imports match on these names and
            never create them for you.
          </p>
          <p className="flex flex-wrap gap-x-4 gap-y-1 text-sm">
            {SETUP_LINKS.map((link) => (
              <Link key={link.label} to={link.to} className="text-[var(--color-primary)] hover:underline">
                {link.label}
              </Link>
            ))}
          </p>
        </div>
      </Card>

      {STEPS.map((step, index) => (
        <StepCard
          key={step.prefix}
          n={index + 2}
          step={step}
          summary={summary.data}
          onImport={() => setActive(step)}
        />
      ))}

      <ImportDialog
        open={active !== null}
        onOpenChange={(open) => {
          if (!open) {
            setActive(null)
            if (canCount) void summary.refetch()
          }
        }}
        what={active?.what ?? ''}
        prefix={active?.prefix ?? '/v1/employees'}
        invalidateKey={active?.invalidateKey ?? []}
        createMissingOption={active?.prefix === '/v1/employees'}
      />
    </div>
  )
}
